import React from "react";
import Card from "@/components/common/card/Card";
import Button from "@/components/common/button/Button";
import Input from "@/components/common/input/Input";
import AlertMessage from "@/components/layout/message/AlertMessage";
import { Plus, Trash2, Save } from "lucide-react";
import styles from "./Recommendations.module.css";
import { useRecommendations } from "./useRecommendations";
import type { recommendationsCardProps } from "./types.props";

const ReadOnlyView: React.FC<{ recommendations: string[] }> = ({
    recommendations,
}) => (
    <>
        {!recommendations || recommendations.length === 0 ? (
            <p className={styles["no-recommendations"]}>
                No hay recomendaciones disponibles.
            </p>
        ) : (
            <ul className={styles["recommendations-list"]}>
                {recommendations.map((rec, index) => (
                    <li key={index} className={styles["recommendation-item"]}>
                        <span className={styles["bullet-point"]} aria-hidden="true">
                            ›
                        </span>
                        {rec}
                    </li>
                ))}
            </ul>
        )}
    </>
);

const EditableView: React.FC<Omit<recommendationsCardProps, "isEditable">> = ({
    recommendations,
    onSave,
    isSaving,
    saveError,
}) => {
    const {
        localRecs,
        newRec,
        setNewRec,
        hasUnsavedChanges,
        handleAdd,
        handleDelete,
        handleSave,
    } = useRecommendations(recommendations, onSave);

    return (
        <div className={styles["editable-container"]}>
            {saveError && (
                <AlertMessage type="error" title="Error al guardar">
                    {saveError.generalMessage}
                </AlertMessage>
            )}

            <ul className={styles["recommendations-list"]}>
                {localRecs.map((rec, index) => (
                    <li
                        key={index}
                        className={`${styles["recommendation-item"]} ${styles["editable-item"]}`}
                    >
                        <span>{rec}</span>
                        <Button
                            variant="icon"
                            onClick={() => handleDelete(index)}
                            aria-label="Eliminar"
                        >
                            <Trash2 size={16} />
                        </Button>
                    </li>
                ))}
            </ul>
            {localRecs.length === 0 && (
                <p>Aún no hay recomendaciones. ¡Añade la primera!</p>
            )}

            <div className={styles["add-recommendation-form"]}>
                <Input
                    id="new-rec"
                    label="recommendation"
                    value={newRec}
                    onChange={(e) => setNewRec(e.target.value)}
                    placeholder="Escribe una nueva recomendación..."
                />
                <Button
                    onClick={handleAdd}
                    leftIcon={<Plus size={16} />}
                    disabled={!newRec.trim()}
                >
                    Añadir
                </Button>
            </div>

            <div className={styles["save-actions"]}>
                <Button
                    onClick={handleSave}
                    variant="primary"
                    isLoading={isSaving}
                    leftIcon={<Save size={16} />}
                    disabled={!hasUnsavedChanges || isSaving}
                >
                    {isSaving ? "Guardando..." : "Guardar cambios"}
                </Button>
            </div>
        </div>
    );
};

const RecommendationsCard: React.FC<recommendationsCardProps> = (props) => {
    return (
        <Card
            title={props.isEditable ? "Gestionar recomendaciones" : "Recomendaciones"}
            icon={props.isEditable ? "✍️" : "💡"}
        >
            {props.isEditable ? (
                <EditableView {...props} />
            ) : (
                <ReadOnlyView recommendations={props.recommendations} />
            )}
        </Card>
    );
};

export default RecommendationsCard;
